import React, { useMemo, useState } from "react";
import { Search, ChevronLeft, ChevronRight, X } from "lucide-react";

export type Column<T> = {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  /** Mobile card primary line marker */
  primary?: boolean;
  /** Hide on mobile */
  hideOnMobile?: boolean;
  className?: string;
  width?: string;
};

type Props<T> = {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  searchableText: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Page size for the legacy client-side pagination. Ignored when
   *  `serverPagination` is true. */
  pageSize?: number;
  emptyState?: React.ReactNode;
  /** Server-side pagination mode (spec docs/specs/dashboard-pagination.md
   *  §7.3). When true:
   *   - `rows` is assumed to already be the current-page slice from the
   *     server (DataTable does NOT call `.slice()` itself).
   *   - The legacy client-side pagination footer is NOT rendered.
   *   - The caller is expected to mount `<PaginationControl>` separately
   *     beneath the table, bound to its own usePagination state.
   *   - The toolbar's "共 N 筆 (原 M)" counter is hidden — the real
   *     total lives on the PaginationControl. The search input still
   *     filters locally within the current page; for server-side search
   *     pass q through to your fetcher and lift the input out of DataTable.
   *  Default: false (preserves backwards compat with all existing
   *  callers). */
  serverPagination?: boolean;
};

export default function DataTable<T>({
  rows,
  columns,
  rowKey,
  searchableText,
  onRowClick,
  pageSize = 10,
  emptyState,
  serverPagination = false,
}: Props<T>): React.ReactElement {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);

  // Local search filter still runs even in server mode (filters within
  // the current page). For full-table server-side search the caller
  // should lift the input out of DataTable; this just exists so the
  // existing free-text-search affordance still works on the current
  // page without forcing every consumer to refactor.
  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) => searchableText(r).toLowerCase().includes(needle));
  }, [rows, q, searchableText]);

  // In server mode the caller already passes the current-page slice;
  // we render `filtered` directly and skip our own paginator.
  const totalPages = serverPagination ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = serverPagination ? 0 : Math.min(page, totalPages - 1);
  const slice = serverPagination
    ? filtered
    : filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);

  return (
    <div className="card overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 border-b border-border px-4 py-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="搜尋…"
            className="input pl-9 pr-8 h-9 text-sm"
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setPage(0);
              }}
              aria-label="清除搜尋"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {/* Counter hidden in server-pagination mode — total lives on
            the caller's PaginationControl. */}
        {!serverPagination && (
          <div className="text-xs text-text-secondary tabular-nums">
            共 {filtered.length} 筆 {q && rows.length !== filtered.length && `(原 ${rows.length})`}
          </div>
        )}
      </div>

      {/* Body */}
      {filtered.length === 0 ? (
        <div className="px-6 py-12 text-center text-sm text-text-secondary">
          {emptyState ?? "沒有資料。"}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {columns.map((c) => (
                    <th key={c.key} className="table-header" style={{ width: c.width }}>
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slice.map((r) => (
                  <tr
                    key={rowKey(r)}
                    className={`table-row ${onRowClick ? "cursor-pointer" : ""}`}
                    onClick={() => onRowClick?.(r)}
                  >
                    {columns.map((c) => (
                      <td key={c.key} className={`table-cell ${c.className ?? ""}`}>
                        {c.render(r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="md:hidden divide-y divide-border">
            {slice.map((r) => (
              <li
                key={rowKey(r)}
                className={`px-4 py-3 ${onRowClick ? "cursor-pointer hover:bg-bg-hover" : ""}`}
                onClick={() => onRowClick?.(r)}
              >
                {columns
                  .filter((c) => !c.hideOnMobile)
                  .map((c) => (
                    <div
                      key={c.key}
                      className={
                        c.primary
                          ? "text-sm font-medium text-text-primary"
                          : "mt-1 text-xs text-text-secondary flex items-center gap-2"
                      }
                    >
                      {!c.primary && <span className="text-text-muted">{c.header}:</span>}
                      <span className={c.primary ? "" : "min-w-0 truncate"}>{c.render(r)}</span>
                    </div>
                  ))}
              </li>
            ))}
          </ul>

          {/* Legacy client-side pagination — skipped entirely in
              server-pagination mode (caller mounts <PaginationControl>
              separately). */}
          {!serverPagination && totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-text-secondary">
              <span className="tabular-nums">
                第 {safePage + 1} / {totalPages} 頁
              </span>
              <div className="flex items-center gap-1">
                <button
                  className="btn-ghost h-7 w-7 p-0 disabled:opacity-30"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  aria-label="上一頁"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  className="btn-ghost h-7 w-7 p-0 disabled:opacity-30"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={safePage >= totalPages - 1}
                  aria-label="下一頁"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
